'use client';

import { CreditMap, Institution, LoanEntry } from './types';

// Month name to number mapping (Portuguese)
const MONTH_MAP: Record<string, number> = {
  'janeiro': 1, 'fevereiro': 2, 'março': 3, 'marco': 3,
  'abril': 4, 'maio': 5, 'junho': 6,
  'julho': 7, 'agosto': 8, 'setembro': 9,
  'outubro': 10, 'novembro': 11, 'dezembro': 12,
  'jan': 1, 'fev': 2, 'mar': 3, 'abr': 4,
  'mai': 5, 'jun': 6, 'jul': 7, 'ago': 8,
  'set': 9, 'out': 10, 'nov': 11, 'dez': 12,
};

const MONTH_LABELS: Record<number, string> = {
  1: 'Janeiro', 2: 'Fevereiro', 3: 'Março', 4: 'Abril',
  5: 'Maio', 6: 'Junho', 7: 'Julho', 8: 'Agosto',
  9: 'Setembro', 10: 'Outubro', 11: 'Novembro', 12: 'Dezembro',
};

function parsePortugueseNumber(str: string): number {
  if (!str) return 0;
  // Handle Portuguese number format: 1.234.567,89 or 150 000,00
  const cleaned = str.trim()
    .replace(/[€\s]/g, '')
    .replace(/\./g, '')   // remove thousand separators
    .replace(',', '.');   // decimal comma → dot
  const val = parseFloat(cleaned);
  return isNaN(val) ? 0 : val;
}

function parseDate(str: string): string {
  if (!str) return '';
  const trimmed = str.trim();
  // yyyy-mm-dd already
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  // dd/mm/yyyy or dd-mm-yyyy
  const match = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (match) {
    const [, d, m, y] = match;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return trimmed;
}

function detectMonthYear(lines: string[]): { mes: number; ano: number } | null {
  for (const line of lines) {
    const lower = line.toLowerCase().trim();

    for (const [monthName, monthNum] of Object.entries(MONTH_MAP)) {
      const regex = new RegExp(`${monthName}\\s+(?:de\\s+)?(\\d{4})`, 'i');
      const m = lower.match(regex);
      if (m) {
        return { mes: monthNum, ano: parseInt(m[1]) };
      }
    }

    const mmyyyy = line.match(/\b(0?[1-9]|1[0-2])[\/\-](20\d{2})\b/);
    if (mmyyyy) {
      return { mes: parseInt(mmyyyy[1]), ano: parseInt(mmyyyy[2]) };
    }

    const yyyymm = line.match(/\b(20\d{2})[-\/](0?[1-9]|1[0-2])\b/);
    if (yyyymm) {
      return { mes: parseInt(yyyymm[2]), ano: parseInt(yyyymm[1]) };
    }
  }
  return null;
}

/** Normalise a string for label matching (remove accents, lowercase, trim) */
function normalise(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/** Check if a text item is a known field label we want to capture */
function matchLabel(text: string): 'produtoFinanceiro' | 'inicio' | 'fim' | 'totalEmDivida' | null {
  const n = normalise(text);
  if (n === 'produto financeiro' || n === 'produto financeiro:') return 'produtoFinanceiro';
  if (n === 'inicio' || n === 'início' || n === 'inicio:' || n === 'início:') return 'inicio';
  if (n === 'fim' || n === 'fim:') return 'fim';
  if (
    n === 'total em divida' ||
    n === 'total em dívida' ||
    n === 'total em divida:' ||
    n === 'total em dívida:'
  )
    return 'totalEmDivida';
  return null;
}

/** Determine if a line looks like an institution name header */
function isInstitutionHeader(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length < 3 || trimmed.length > 120) return false;

  const n = normalise(trimmed);

  // Skip known field labels and structural keywords
  const skipWords = [
    'produto', 'financeiro', 'inicio', 'fim', 'total', 'divida',
    'data', 'valor', 'saldo', 'mes', 'ano', 'banco de portugal',
    'mapa', 'credito', 'emprestimos', 'instituicao',
    'pagina', 'page', 'ref', 'nif', 'numero',
    'tipo', 'responsabilidade', 'negociacao', 'montantes',
    'devedor', 'garantias', 'prestacao', 'periodicidade',
    'vencido', 'potencial', 'abatido', 'ativo', 'litigio',
    'devedores', 'contrato', 'incumprimento', 'entrada',
    'informacao comunicada', 'informação comunicada',
  ];
  if (skipWords.some((w) => n.includes(w))) return false;

  // Must contain at least one letter
  if (!/[a-záàâãéèêíìîóòôõúùûç]/i.test(trimmed)) return false;

  // Should not be purely numeric or a date
  if (/^\d+[\.,]?\d*$/.test(trimmed)) return false;
  if (/\d{4}-\d{2}-\d{2}/.test(trimmed)) return false;
  if (/\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}/.test(trimmed)) return false;

  // Starts with uppercase letter
  if (trimmed[0] === trimmed[0].toUpperCase() && /[A-ZÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ]/.test(trimmed[0])) {
    return true;
  }
  return false;
}

/**
 * Try to extract a bank name from the pattern:
 * "Informação comunicada pela instituição: BANK NAME (XXXX)"
 * Returns the bank name string or null if not matched.
 */
function extractInstitutionFromHeader(rowFull: string): string | null {
  const n = normalise(rowFull);
  // Match: "informacao comunicada pela instituicao: BANK NAME"
  const match = n.match(/informa[cç][aã]o\s+comunicada\s+pela\s+institui[cç][aã]o\s*:\s*(.+)/);
  if (match) {
    // Get the original text after the colon (preserve original casing)
    const colonIdx = rowFull.indexOf(':');
    if (colonIdx !== -1) {
      return rowFull.slice(colonIdx + 1).trim();
    }
  }
  return null;
}

interface TextItem {
  text: string;
  x: number;
  y: number;
  page: number;
}

/**
 * Main PDF parsing function.
 * Uses a label-based approach: scans for the exact field labels
 * "Produto financeiro", "Início", "Fim", "Total em dívida" and captures
 * the value that follows each label on the same row (or immediately after).
 */
export async function parseBancodePortugalPDF(file: File): Promise<CreditMap> {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const allItems: TextItem[] = [];

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1 });
    const textContent = await page.getTextContent();

    for (const item of textContent.items) {
      if ('str' in item && item.str.trim()) {
        const transform = item.transform as number[];
        const x = transform[4];
        const y = viewport.height - transform[5];
        allItems.push({ text: item.str, x, y, page: pageNum });
      }
    }
  }

  // ── Detect month/year from top-right of first page ──────────────────────
  const firstPageItems = allItems.filter((i) => i.page === 1);
  const maxX = firstPageItems.length ? Math.max(...firstPageItems.map((i) => i.x)) : 0;
  const topRightTexts = firstPageItems
    .filter((i) => i.x > maxX * 0.55 && i.y < 150)
    .map((i) => i.text);

  let monthYear = detectMonthYear(topRightTexts);
  if (!monthYear) monthYear = detectMonthYear(allItems.map((i) => i.text));
  if (!monthYear) {
    const fnLower = file.name.toLowerCase();
    for (const [monthName, monthNum] of Object.entries(MONTH_MAP)) {
      if (fnLower.includes(monthName)) {
        const yearMatch = file.name.match(/20\d{2}/);
        if (yearMatch) {
          monthYear = { mes: monthNum, ano: parseInt(yearMatch[0]) };
          break;
        }
      }
    }
  }
  if (!monthYear) {
    const now = new Date();
    monthYear = { mes: now.getMonth() + 1, ano: now.getFullYear() };
  }

  // ── Group items into rows (items within 4px Y are on the same row) ───────
  const sorted = [...allItems].sort((a, b) =>
    a.page !== b.page ? a.page - b.page : a.y !== b.y ? a.y - b.y : a.x - b.x
  );

  interface Row {
    items: TextItem[];
    y: number;
    page: number;
  }

  const rows: Row[] = [];
  let currentRow: TextItem[] = [];
  let lastY = -9999;
  let lastPage = -1;

  for (const item of sorted) {
    if (item.page !== lastPage || Math.abs(item.y - lastY) > 4) {
      if (currentRow.length > 0) {
        rows.push({ items: currentRow, y: lastY, page: lastPage });
      }
      currentRow = [item];
      lastY = item.y;
      lastPage = item.page;
    } else {
      currentRow.push(item);
    }
  }
  if (currentRow.length > 0) rows.push({ items: currentRow, y: lastY, page: lastPage });

  // ── Label-based extraction ────────────────────────────────────────────────
  //
  // Strategy:
  //   For each row, check every item for a known label.
  //   When a label is found, the value is the next non-label item on the same row,
  //   or (for "Total em dívida") the first numeric item on the same row.
  //
  // Loan block lifecycle:
  //   A new block starts when "Produto financeiro" is found.
  //   The block is committed when all 4 fields are collected OR when a new
  //   "Produto financeiro" label is encountered.
  //
  // Institution detection:
  //   A row with a single item that passes isInstitutionHeader() starts a new institution.

  const institutions: Institution[] = [];
  let currentInstitution: Institution | null = null;

  // Current loan block being assembled
  let pendingProduto: string | null = null;
  let pendingInicio: string | null = null;
  let pendingFim: string | null = null;
  let pendingTotal: number | null = null;

  function commitLoan() {
    if (
      currentInstitution &&
      pendingProduto !== null &&
      pendingInicio !== null &&
      pendingFim !== null &&
      pendingTotal !== null
    ) {
      const loan: LoanEntry = {
        id: `loan-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        produtoFinanceiro: pendingProduto,
        inicio: pendingInicio,
        fim: pendingFim,
        totalEmDivida: pendingTotal,
      };
      currentInstitution.loans.push(loan);
    }
    pendingProduto = null;
    pendingInicio = null;
    pendingFim = null;
    pendingTotal = null;
  }

  function commitInstitution() {
    if (currentInstitution) {
      // Commit any pending loan before closing institution
      commitLoan();
      if (currentInstitution.loans.length > 0) {
        currentInstitution.totalEmDivida = currentInstitution.loans.reduce(
          (s, l) => s + l.totalEmDivida,
          0
        );
        institutions.push(currentInstitution);
      }
      currentInstitution = null;
    }
  }

  for (const row of rows) {
    const rowTexts = row.items.map((i) => i.text.trim()).filter(Boolean);
    const rowFull = rowTexts.join(' ').trim();

    if (!rowFull) continue;

    // ── Institution header detection ──────────────────────────────────────
    // PRIMARY: detect "Informação comunicada pela instituição: BANK NAME"
    const institutionFromHeader = extractInstitutionFromHeader(rowFull);
    if (institutionFromHeader) {
      commitInstitution();
      currentInstitution = {
        id: `inst-${institutionFromHeader
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')
          .slice(0, 30)}-${Date.now()}`,
        nome: institutionFromHeader,
        loans: [],
        totalEmDivida: 0,
      };
      continue;
    }

    // FALLBACK: A row with 1–6 tokens that looks like a bank name and has no field labels
    const hasLabel = rowTexts.some((t) => matchLabel(t) !== null);
    if (!hasLabel && rowTexts.length >= 1 && rowTexts.length <= 6) {
      if (isInstitutionHeader(rowFull)) {
        // Only use the fallback heuristic when there is no active institution yet.
        // If we already have a currentInstitution, trust it to continue across pages
        // rather than risk misidentifying a page header/footer as a new bank name.
        if (!currentInstitution) {
          currentInstitution = {
            id: `inst-${rowFull
              .toLowerCase()
              .replace(/\s+/g, '-')
              .replace(/[^a-z0-9-]/g, '')
              .slice(0, 30)}-${Date.now()}`,
            nome: rowFull,
            loans: [],
            totalEmDivida: 0,
          };
        }
        continue;
      }
    }

    // ── Field label scanning ──────────────────────────────────────────────
    // Walk through items in X order (already sorted) looking for labels
    const itemsSortedByX = [...row.items].sort((a, b) => a.x - b.x);

    for (let i = 0; i < itemsSortedByX.length; i++) {
      const item = itemsSortedByX[i];
      const label = matchLabel(item.text);

      if (label === null) continue;

      // Value = next item(s) on the same row after the label
      // For multi-word labels that may be split across items, we already
      // normalise the full item text, so a single item "Produto financeiro"
      // is matched directly. If the PDF splits it, we handle below.

      if (label === 'produtoFinanceiro') {
        // If we already have a pending produto, commit the previous loan first
        if (pendingProduto !== null) commitLoan();

        // Collect all remaining items on this row as the product name
        const valueItems = itemsSortedByX
          .slice(i + 1)
          .map((it) => it.text.trim())
          .filter((t) => t && matchLabel(t) === null);
        if (valueItems.length > 0) {
          pendingProduto = valueItems.join(' ').trim();
        }
        break; // product name consumes the rest of the row
      }

      if (label === 'inicio') {
        // Value is the next item after the label
        const next = itemsSortedByX[i + 1];
        if (next) {
          pendingInicio = parseDate(next.text.trim());
        }
        // Don't break — "Fim" may also be on the same row
        i++; // skip the value item
        continue;
      }

      if (label === 'fim') {
        const next = itemsSortedByX[i + 1];
        if (next) {
          pendingFim = parseDate(next.text.trim());
        }
        i++;
        continue;
      }

      if (label === 'totalEmDivida') {
        // Value is the next item — should be a number
        const next = itemsSortedByX[i + 1];
        if (next) {
          const amount = parsePortugueseNumber(next.text);
          if (!isNaN(amount)) {
            pendingTotal = amount;
            // When we have all 4 fields, commit immediately
            if (
              pendingProduto !== null &&
              pendingInicio !== null &&
              pendingFim !== null
            ) {
              commitLoan();
            }
          }
        }
        i++;
        continue;
      }
    }
  }

  // Commit any remaining open institution/loan
  commitInstitution();

  const totalGeral = institutions.reduce((s, i) => s + i.totalEmDivida, 0);
  const mesLabel = `${MONTH_LABELS[monthYear.mes]} ${monthYear.ano}`;

  return {
    id: `map-${monthYear.ano}-${String(monthYear.mes).padStart(2, '0')}`,
    mes: monthYear.mes,
    ano: monthYear.ano,
    mesLabel,
    institutions,
    totalGeral,
    fileName: file.name,
    parsedAt: new Date().toISOString(),
  };
}
